// ============================================================================
// Estimated Delivery Date — date math + hide logic
// ----------------------------------------------------------------------------
// Loaded from sections/estimated-delivery-date.liquid. That section emits five
// globals into the page:
//   - cutOffTimes       : array of per-weekday cutoff blocks (0=Sun ... 6=Sat)
//   - eddSettings       : hide-EDD configuration (tags, vendors, POD switch)
//   - currentProduct    : server-snapshot of {tags, vendor, type} for the PDP
//   - podSettings       : extra calendar days to add to the EDD for POD orders
//   - podExists         : 'yes' if PDP product OR any cart item is POD (server)
//   - eddStyles, eddLabel : visual config
//
// The end result is an Alpine store at $store.edd that the two snippets
// (snippets/estimated-delivery-date.liquid and -oversold.liquid) read from.
//
// Two cooperating responsibilities live in this file:
//   1. Date math
//      Compute the ship date and the EDD date the customer should expect, plus
//      the countdown that says "Order within X hr Y min" until the next cutoff.
//      See the "Date math" section below for the full set of business rules.
//   2. Hide gate
//      Expose a reactive `shouldHide` getter that the snippet templates use to
//      decide whether to render at all. This handles per-product tag/vendor/POD
//      checks AND reacts to cart changes (e.g. a POD product gets added).
// ============================================================================

if (cutOffTimes && cutOffTimes.length > 0) {

  // ==========================================================================
  // Time helpers — keep everything in PST
  // ==========================================================================

  // Returns "now" as a Date whose local-time components match the current PST
  // (America/Los_Angeles) wall-clock time. Using Intl.DateTimeFormat parts to
  // build the Date avoids the bug in the prior implementation where
  // `new Date(toLocaleString())` would re-interpret PST values as the browser's
  // own local time, drifting by the browser's UTC offset from PST. After this,
  // every `setHours`/`setDate` call in this file operates in PST cleanly.
  const currentPSTDateTime = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false
    }).formatToParts(new Date()).reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});

    return new Date(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24,
      Number(parts.minute),
      Number(parts.second)
    );
  };

  // Sun (0) and Sat (6) are weekends. Mon-Fri are business days.
  // (Prior code had this wrong — it treated Friday as a weekend day and missed
  // Sunday entirely.)
  const isWeekend = (day) => day === 0 || day === 6;

  // Find the section's cut-off block for the given weekday, or null. The block
  // is configurable per-day in the theme customizer.
  const cutoffFor = (weekday) =>
    cutOffTimes.find((c) => Number(c.day_of_week) === Number(weekday)) || null;

  // Mutate `dt` to point at the cutoff time-of-day for whatever weekday it
  // already represents. If a weekday has no block (shouldn't happen with the
  // default 7-day preset), fall back to 10:00 PST so we never crash.
  const applyCutoff = (dt) => {
    const cut = cutoffFor(dt.getDay());
    dt.setHours(cut ? Number(cut.cut_off_time_hours) : 10);
    dt.setMinutes(cut ? Number(cut.cut_off_time_minuts) : 0);
    dt.setSeconds(0);
    dt.setMilliseconds(0);
    return dt;
  };

  const addCalendarDays = (dt, n) => {
    const resultDate = new Date(dt);

    let addedDays = 0;

    while (addedDays < n) {
      resultDate.setDate(resultDate.getDate() + 1);
      const day = resultDate.getDay();
      if (day !== 0 && day !== 6) { // 0 = Sunday, 6 = Saturday
        addedDays++;
      }
    }

    // d.setDate(d.getDate() + n);
    return resultDate;
  };

  // Return the next business day strictly AFTER `dt` (i.e. tomorrow if tomorrow
  // is Mon-Fri, otherwise skip ahead until we land on a weekday).
  const nextBusinessDay = (dt) => {
    let d = addCalendarDays(dt, 1);
    while (isWeekend(d.getDay())) d = addCalendarDays(d, 1);
    return d;
  };

  // POD type detection — kept in sync with the Liquid logic in
  // sections/estimated-delivery-date.liquid. Matches "POD " or "POD: " in the
  // product type (case-insensitive) so we catch "POD Apparel", "POD: Mug", etc.
  const POD_REGEX = /POD:? /i;
  const isPodType = (typeStr) => POD_REGEX.test(typeStr || '');

  // Convert a millisecond duration into {hh, mm, ss, ms} for the countdown UI.
  const milisecondToText = (diff) => {
    const ms = diff % 1000;
    const totalSeconds = Math.floor(diff / 1000);

    const ss = totalSeconds % 60;
    const mm = Math.floor(totalSeconds / 60) % 60;
    const totalHours = Math.floor(totalSeconds / 3600);

    // let dd = Math.floor(totalHours / 24);   // days
    const hh = totalHours % 24;               // remaining hours

    return {
        // dd, // Avoid days, only show in hours loop
        hh,
        mm,
        ss
    };
}

  // ==========================================================================
  // Date math
  // --------------------------------------------------------------------------
  // Two independent computations happen here, both anchored to "now" in PST:
  //
  //   ship date — when the order leaves our warehouse.
  //     Rule: if today is a business day (Mon-Fri) AND now < today's cutoff
  //           time, ship today. Otherwise ship on the next business day.
  //
  //   countdown target — what time we count down to in the "Order within X
  //                      hr Y min" UI.
  //     Rule: if now < today's cutoff, count down to today's cutoff. Otherwise
  //           count down to tomorrow's (literal next calendar day's) cutoff.
  //     This always produces a countdown < 24 hr by construction.
  //
  // The two are deliberately independent: each calendar day has its own cutoff
  // setting that controls the countdown UI, but weekend days never promote
  // themselves into a "ship day". So Saturday at 5 PM, for example, shows a
  // countdown to Sunday's 10 AM cutoff but ships Monday regardless.
  // ==========================================================================

  const now = currentPSTDateTime();
  const todayCutoff = applyCutoff(new Date(now));
  const beforeTodaysCutoff = now.getTime() < todayCutoff.getTime();
  const todayIsBiz = !isWeekend(now.getDay());

  // Ship date — see rule above.
  const shipDate = (todayIsBiz && beforeTodaysCutoff)
    ? new Date(now)
    : nextBusinessDay(now);

  // Number of calendar days between today (midnight) and the ship date. We use
  // this offset rather than the date itself in the eddDateText getter, so the
  // math is "calendar days from today" — easier to reason about than building
  // off the shipDate Date object which has time-of-day baggage.
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const shipDateMidnight = new Date(shipDate.getFullYear(), shipDate.getMonth(), shipDate.getDate());
  // const eddShipOffsetCalendarDays = Math.round(
  //   (shipDateMidnight.getTime() - todayMidnight.getTime()) / 86400000
  // );

  // -1 mean include current days
  const eddShipOffsetCalendarDays = beforeTodaysCutoff ? -1 : 0;

  // Countdown — see rule above. Always < 24 hr.
  const countdownTarget = beforeTodaysCutoff
    ? todayCutoff
    : applyCutoff(addCalendarDays(now, 1));
  const remainingCutoffTime = countdownTarget.getTime() - now.getTime();
  const remainingCutoffTimeText = milisecondToText(remainingCutoffTime);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ==========================================================================
  // Alpine store
  // ==========================================================================

  document.addEventListener('alpine:init', () => {
    Alpine.store('edd', {
      eddSettings,
      podSettings,
      podExists,
      eddStyles,
      eddLabel,
      postalCode: null,
      newPostalCode: null,
      daysInTransit: null,
      remainingCutoffTimeText,
      isEditPostalCode: false,
      isLoading: true,

      init() {
        // Try the in-session cache first — postal code + transit days are
        // expensive to refetch and don't change mid-session for a normal user.
        const cachedEddData = window.sessionStorage.getItem('LIF-PARAMS-edd-days-in-transit');
        if (cachedEddData) {
          const { postalCode, days } = JSON.parse(cachedEddData);
          this.postalCode = postalCode;
          this.newPostalCode = postalCode;
          this.daysInTransit = days;
          this.isLoading = false;
        } else {
          // No cache → IP-geolocate the visitor, then fetch transit days for
          // their ZIP from our backend. If ipinfo fails (rate-limited or down)
          // we just leave the widget in `isLoading: true` and log; the user
          // can still edit the postal code manually via the modal.
          fetch('https://ipinfo.io/?callback')
            .then((response) => response.json())
            .then((response) => {
              this.postalCode = response.postal;
              this.newPostalCode = response.postal;

              if (!this.postalCode) {
                this.postalCode = '00001';
                this.newPostalCode = '00001';
              }
              this.updateEddByPortalCode();
            })
            .catch(() => {
              console.error('Error while fetching ipinfo!')

              this.postalCode = '00001';
              this.newPostalCode = '00001'
              
              this.updateEddByPortalCode();;
            });
        }
      },

      updateEddByPortalCode() {
        if (!this.newPostalCode || this.newPostalCode === '') {
          return false;
        }

        // Fetch days-in-transit from the LIF backend for the entered ZIP.
        // Response shape: { data: { days_in_transit: "<number>" } }
        fetch('https://cron.loveinfaith.work/store-backend/edd/getEddByPostalCode?postal_code=' + this.newPostalCode)
          .then((response) => response.json())
          .then((response) => {
            this.postalCode = this.newPostalCode;

            if (response.data && response.data.days_in_transit) {
              this.daysInTransit = parseInt(response.data.days_in_transit);
            } else {
              this.daysInTransit = null;
            }

            // Cache so subsequent PDPs in the same session don't refetch.
            const cacheData = {
              postalCode: this.newPostalCode,
              days: this.daysInTransit
            };
            window.sessionStorage.setItem('LIF-PARAMS-edd-days-in-transit', JSON.stringify(cacheData));
            this.isLoading = false;
          });
      },

      editPostalCode() {
        this.newPostalCode = this.postalCode;
        this.isEditPostalCode = true;
      },

      updatePostalCode() {
        if (!this.newPostalCode || this.newPostalCode === '') {
          return false;
        }
        this.updateEddByPortalCode();
        this.isEditPostalCode = false;
      },

      // ----------------------------------------------------------------------
      // shouldHide — reactive gate consumed by the snippets via x-if.
      // ----------------------------------------------------------------------
      // Hide the EDD when ANY of these are true:
      //   - The current PDP product has a tag in eddSettings.hideEddProductTags
      //   - The current PDP product has a vendor in eddSettings.hideEddVendors
      //   - eddSettings.hideEddOnPodProducts is true AND the current PDP
      //     product is a POD product type
      //   - ANY cart item has a tag in eddSettings.hideEddProductTags
      //   - eddSettings.hideEddOnPodProducts is true AND any cart item is POD
      //
      // The cart check reads $store('cart').cartItems, which Alpine treats as
      // a reactive dependency. So if a customer adds a POD product to cart
      // while sitting on a non-POD PDP, this getter re-evaluates and the EDD
      // disappears without a page reload.
      get shouldHide() {
        const { hideEddProductTags, hideEddVendors, hideEddOnPodProducts } = this.eddSettings;

        // --- Current PDP product checks --------------------------------------
        if (currentProduct.tags && Array.isArray(currentProduct.tags)) {
          if (hideEddProductTags.some((t) => t && currentProduct.tags.includes(t))) {
            return true;
          }
        }
        if (currentProduct.vendor && hideEddVendors.includes(currentProduct.vendor)) {
          return true;
        }
        if (hideEddOnPodProducts && isPodType(currentProduct.type)) {
          return true;
        }

        // --- Cart-level checks ----------------------------------------------
        // Alpine.store('cart') may not exist yet on first paint; bail out
        // cleanly (don't hide just because the cart store hasn't init'd).
        const cart = Alpine.store('cart');
        if (cart && Array.isArray(cart.cartItems)) {
          for (const item of cart.cartItems) {
            if (hideEddOnPodProducts && isPodType(item.product_type)) {
              return true;
            }
            if (Array.isArray(item.tags) &&
                hideEddProductTags.some((t) => t && item.tags.includes(t))) {
              return true;
            }
          }
        }

        return false;
      },

      // ----------------------------------------------------------------------
      // eddDateText — the formatted "Wed, May 21"-style EDD shown to the user.
      // ----------------------------------------------------------------------
      // Composition (all in calendar days, no business-day skipping — the
      // ship date already accounts for weekends, and carriers DO transit on
      // weekends):
      //   total = shipOffset + daysInTransit (+ podSettings if POD)
      // Then if the resulting date lands on Sunday, push it to Monday because
      // our carriers transit 7 days a week but DELIVER Mon-Sat.
      //
      // Note: the prior implementation subtracted 1 from daysInTransit and
      // then walked business days, which double-counted weekends. We now use
      // calendar days throughout for transit, with one targeted Sunday push.
      get eddDateText() {
        if (!this.daysInTransit) return null;

        let total = eddShipOffsetCalendarDays + this.daysInTransit;

        if (this.podExists === 'yes') {
          total += Number(this.podSettings || 0);
        }

        let eddDate = addCalendarDays(now, total);

        const weekDay = weekDays[eddDate.getDay()];
        const month = monthNames[eddDate.getMonth()];
        const date = eddDate.getDate();

        return `${weekDay}, ${month} ${date}`;
      },

      get hourText() {
        return this.remainingCutoffTimeText.hh > 1 ? 'hrs' : 'hr';
      },

      get minutText() {
        return this.remainingCutoffTimeText.mm > 1 ? 'mins' : 'min';
      },

      // Oversold ETA — a separate rendering path used by the oversold snippet.
      // Takes a string date from the product metafield. If the metafield is
      // missing, malformed, or already in the past, we fall back to "today + 7
      // calendar days" as a safe guaranteed-ship promise.
      getOverSoldETA(dateText) {
        const currentDate = currentPSTDateTime();
        let eddDate = null;

        if (dateText) {
          eddDate = new Date(dateText);
        }

        if (!eddDate ||
            isNaN(eddDate.getTime()) ||
            currentDate.toDateString() === eddDate.toDateString() ||
            currentDate.getTime() > eddDate.getTime()) {
          currentDate.setDate(currentDate.getDate() + 7);
          eddDate = currentDate;
        }

        const weekDay = weekDays[eddDate.getDay()];
        const month = monthNames[eddDate.getMonth()];
        const date = eddDate.getDate();

        return `${weekDay}, ${month} ${date}`;
      }
    });
  });
}
